import { Reducer, useEffect, useReducer, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { isEmpty, isNull, noop, size, sortBy } from 'lodash';

import {
  ALL_RECORDS_CQL,
  FACET,
  FACET_LIMIT,
  FacetsDto,
  getLanguageOptions,
  getLocationOptions,
  MultiselectDropdownItem,
  useUrlManager,
} from '@app/components';
import { useLocationsFetch } from '@app/hooks';
import { makeRequest } from '@app/utils';
import { langMessages } from '@app/translations';
import { ENGLISH_LOCALE_KEY } from '@app/constants';

import {
  defaultUnmappedLimitersValue,
  limitersDefaultValue,
  SearchLimiters,
  UnmappedParsedSearchLimiters,
} from '../constants';

const sortSearchLimiterOptions = (
  options: MultiselectDropdownItem[],
  locale = ENGLISH_LOCALE_KEY,
  priorityOptionSortFunc = noop
) =>
  sortBy(options, [
    priorityOptionSortFunc,
    option => option.label.toLocaleLowerCase(locale),
  ]);

const mapSourceTypesToDropdownItems = (sourceTypes: string[] = []) => {
  const options = sourceTypes.map(type => ({
    label: type,
    value: type,
    id: type,
  }));

  return sortSearchLimiterOptions(options);
};

export const useAdvancedSearchLimiters = () => {
  const { $t } = useIntl();
  const { getSearchStateFromUrl } = useUrlManager();
  const [unmappedLimiters, setUnmappedLimiters] =
    useState<UnmappedParsedSearchLimiters>({
      ...defaultUnmappedLimitersValue,
      ...getSearchStateFromUrl().facets,
    });

  const sourceTypesQuery = useQuery(
    ['sourceTypes'],
    () => makeRequest<string[]>({ url: '/opac-inventory/source-types' }),
    { refetchOnMount: false }
  );
  const locationsQuery = useLocationsFetch();
  const facetsQuery = useQuery(
    ['facetQuery'],
    () =>
      makeRequest<FacetsDto>({
        url: `/search/instances/facets?facet=sourceTypes:${FACET_LIMIT},items.effectiveLocationId:${FACET_LIMIT},languages:${FACET_LIMIT}&query=${ALL_RECORDS_CQL}`,
      }),
    {
      refetchOnMount: false,
      select: ({ facets }) => ({
        languages: facets.languages?.values,
        locations: facets['items.effectiveLocationId']?.values,
        sourceTypes: facets.sourceTypes?.values,
      }),
    }
  );

  const sourceType = facetsQuery.data?.sourceTypes?.map(item => item.id);
  const sourceTypeItems = mapSourceTypesToDropdownItems(sourceType);
  const languageItems = sortSearchLimiterOptions(
    getLanguageOptions($t, facetsQuery.data?.languages),
    localStorage.getItem('locale') ?? ENGLISH_LOCALE_KEY,
    option => option.value !== 'eng'
  );
  const locationsItems = sortSearchLimiterOptions(
    getLocationOptions(locationsQuery.data, facetsQuery.data?.locations)
  );
  const [searchLimiters, setSearchLimiters] = useReducer<
    Reducer<SearchLimiters, Partial<SearchLimiters>>
  >(
    (prevState, newState) => ({
      ...prevState,
      ...newState,
    }),
    limitersDefaultValue
  );

  useEffect(() => {
    if (
      !isEmpty(sourceTypeItems) &&
      !isEmpty(unmappedLimiters.sourceTypes) &&
      !isNull(unmappedLimiters.sourceTypes)
    ) {
      setSearchLimiters({
        [FACET.FORMAT]: mapSourceTypesToDropdownItems(
          unmappedLimiters.sourceTypes
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size(sourceTypeItems)]);

  useEffect(() => {
    if (
      languageItems.length &&
      !isEmpty(unmappedLimiters.languages) &&
      !isNull(unmappedLimiters.languages)
    ) {
      setSearchLimiters({
        [FACET.LANGUAGE]: getLanguageOptions(
          $t,
          unmappedLimiters.languages.map(code => ({
            id: code,
            totalRecords: 0,
          }))
        ),
      });
    }
  }, [$t, languageItems.length, unmappedLimiters.languages]);

  useEffect(() => {
    if (
      size(locationsItems) &&
      size(locationsQuery.data) &&
      !isEmpty(unmappedLimiters[FACET.LOCATION])
    ) {
      setSearchLimiters({
        [FACET.LOCATION]: getLocationOptions(
          locationsQuery.data,
          unmappedLimiters[FACET.LOCATION]?.map(id => ({
            id,
            totalRecords: 0,
          })),
          $t(langMessages.undefined)
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    locationsItems.length,
    locationsQuery.data,
    unmappedLimiters?.[FACET.LOCATION],
  ]);

  //Immediately reset state back to default one after setting null for clear all functionality
  useEffect(() => {
    if (isNull(unmappedLimiters.sourceTypes)) {
      setUnmappedLimiters(defaultUnmappedLimitersValue);
    }
  }, [unmappedLimiters]);

  return {
    searchLimiters,
    sourceTypeItems,
    languageItems,
    locationsItems,
    unmappedLimiters,
    areSourceTypesLoading: sourceTypesQuery.isLoading,
    areLanguagesLoading: facetsQuery.isLoading,
    areLocationsLoading: facetsQuery.isLoading ?? locationsQuery.isLoading,
    setSearchLimiters,
    setUnmappedLimiters,
  };
};
